import {ButtonProps, closeDialog, KeyboardShortcut, openDialog} from 'platform/components';
import {Box, HStack} from 'platform/foundation';

import {useState} from 'react';

import {equals, isNil, not} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  GetMetadaServiceCaseApiResponse,
  PostWorkBasketItemFromLabourCatalogApiArg,
  useDeleteWorkBasketItemsMutation,
  useGetServiceOrderJobQuery,
  useGetServiceOrderQuery,
  useGetServiceOrderVariantQuery,
  useGetWorkBasketItemsQuery,
  usePatchOrderItemForWorkMutation,
  usePatchWorkBasketItemMutation,
  usePostServiceItemsLabourSettingsMutation,
  usePostWorkBasketItemFromLabourCatalogMutation,
  usePostWorkBasketItemsToOrderMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {
  EitherQuantityOrError,
  FullScreenModal,
  handleApiError,
  queryParams,
  useBasketItemsQuantityState,
} from '@dms/shared';
import {
  AfterSalesLabourBasket,
  LabourBasketItemEditingDetails,
  useAfterSalesHeaderParameters,
  useBasketMechanic,
} from '@dms/shared-aftersales';

import {buildArray, RequiredTestIdProps, suffixTestId, useQueryState} from 'shared/web';

import {focusGridPart} from 'features/eag-grid';

import {BulkEditItem} from '../../../../../components/BulkEditItem/BulkEditItem';
import {EditLabourItem} from '../../../../../components/EditLabourItem/EditLabourItem';
import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';
import {LabourBasketItemType} from '../../../../../types/LabourBasketItemType';
import {WORK_CATALOGUE_GRID_CODE} from '../constants/WORK_CATALOGUE_GRID_CODE';
import {ItemListCard} from './ItemListCard';

interface AddLabourModalProps extends RequiredTestIdProps {
  onSubmit: VoidFunction;
  serviceCase: GetMetadaServiceCaseApiResponse;
}

export function AddLabourModal(props: AddLabourModalProps) {
  const {serviceCaseId, orderId, addLabourJobId} = useWorkshopUrl();
  const [, , closeModal] = useQueryState(queryParams.SERVICE_CASE_ADD_LABOUR_JOB_ID);

  const {data: serviceJob} = useGetServiceOrderJobQuery({
    serviceCaseId,
    serviceOrderId: orderId,
    serviceJobId: addLabourJobId,
  });
  const {data: serviceOrder} = useGetServiceOrderQuery({serviceCaseId, serviceOrderId: orderId});

  const headerParameters = useAfterSalesHeaderParameters({
    numberLabel: i18n.t('entity.warehouse.labels.serviceOrder'),
    number: serviceOrder?.number,
    customerLabel: i18n.t('entity.invoice.labels.recipientName'),
    customerId: serviceOrder?.billingInformation?.customerInformation?.customerId,
    vehicleId: props.serviceCase?.vehicleId,
  });

  const {data: orderVariant} = useGetServiceOrderVariantQuery(
    {serviceOrderVariantId: serviceOrder?.serviceOrderVariantId ?? ''},
    {skip: isNil(serviceOrder?.serviceOrderVariantId)}
  );

  const {
    data: basket,
    isLoading: isBasketLoading,
    isFetching: isRefreshingBasket,
    isError: hasBasketError,
  } = useGetWorkBasketItemsQuery({
    serviceCaseId,
    serviceOrderId: orderId,
    serviceJobId: addLabourJobId,
  });

  const [postWorkBasketItemFromLabourCatalog, {isLoading: isAddingItemsToBasket}] =
    usePostWorkBasketItemFromLabourCatalogMutation();

  const [deleteWorkBasketItems, {isLoading: isDeletingBasketItems}] =
    useDeleteWorkBasketItemsMutation();

  const [postWorkBasketItemsToOrder, {isLoading: isCheckingOutBasket}] =
    usePostWorkBasketItemsToOrderMutation();

  const [patchWorkBasketItem, {isLoading: isChangingQuantity}] = usePatchWorkBasketItemMutation();

  const [, {isLoading: isEditingLabourItem}] = usePatchOrderItemForWorkMutation({
    fixedCacheKey: 'edit-service-order-work-item',
  });

  const [, {isLoading: isBulkEditingLabourItems}] = usePostServiceItemsLabourSettingsMutation({
    fixedCacheKey: 'bulk-edit-service-order-work-item',
  });

  const basketItems = basket?.workBasketItem ?? [];

  const isBasketEmpty = isNilOrEmpty(basketItems);

  const [focusTrigger, setFocusTrigger] = useState(0);

  const {setBasketItemQuantity, hasInvalidBasketItems, hasUnsettledQuantity} =
    useBasketItemsQuantityState(basketItems);

  const hasPendingRequest =
    isRefreshingBasket ||
    isAddingItemsToBasket ||
    isCheckingOutBasket ||
    isDeletingBasketItems ||
    isChangingQuantity ||
    isEditingLabourItem ||
    isBulkEditingLabourItems;

  const {mechanics, areMechanicsLoading, mechanicPayload, inputMechanicId, handleMechanicChange} =
    useBasketMechanic({
      authorizationProfileId: orderVariant?.authorizationProfileId,
      assignedMechanicId: serviceJob?.assignMechanics?.[0].id,
      competenceId: serviceJob?.competencyId,
    });

  const handleCancel = () => {
    deleteWorkBasketItems({
      serviceCaseId,
      serviceOrderId: orderId,
      serviceJobId: addLabourJobId,
      body: {serviceItemId: basketItems?.map((item) => item.id) ?? []},
    })
      .unwrap()
      .catch(handleApiError)
      .finally(closeModal);
  };

  const handleCheckout = () => {
    postWorkBasketItemsToOrder({
      serviceCaseId,
      serviceOrderId: orderId,
      serviceJobId: addLabourJobId,
      body: mechanicPayload
        ? {
            assignMechanics: [mechanicPayload],
          }
        : undefined,
    })
      .unwrap()
      .then(() => {
        closeModal();
        props.onSubmit();
      })
      .catch(handleApiError);
  };

  const handleAddLabourItem = async (arg: PostWorkBasketItemFromLabourCatalogApiArg) =>
    await postWorkBasketItemFromLabourCatalog(arg)
      .unwrap()
      .then(() => setFocusTrigger((prev) => prev + 1))
      .catch(handleApiError);

  const handleQuantityChange = async (itemId: string, quantity: EitherQuantityOrError) => {
    setBasketItemQuantity(itemId, quantity);

    if (isNil(quantity.newQuantity) || quantity.hasError) {
      return;
    }

    const basketItem = basketItems?.find((item) => equals(item.id, itemId));
    const hasQuantityChanged = not(equals(basketItem?.quantity, quantity.newQuantity));

    if (not(hasQuantityChanged)) {
      return;
    }

    await patchWorkBasketItem({
      serviceCaseId,
      serviceOrderId: orderId,
      serviceJobId: addLabourJobId,
      serviceItemId: itemId,
      body: {
        quantity: quantity.newQuantity,
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleEditLabourItem = ({
    itemId,
    itemName,
    priceTypeName,
  }: LabourBasketItemEditingDetails) => {
    if (isNil(itemId)) {
      return;
    }

    openDialog(
      <EditLabourItem
        itemId={itemId}
        onClose={() => closeDialog('editBasketItem')}
        serviceCaseId={serviceCaseId}
        serviceOrderId={orderId}
        serviceJobId={addLabourJobId}
        data-testid={suffixTestId('editItem', props)}
      />,
      {
        title: `${itemName ?? ''} ${priceTypeName ? `(${priceTypeName})` : ''}`,
        id: 'editBasketItem',
        withAdditionalFooter: true,
        size: 'large',
      }
    );
  };

  const handleDeleteLabourItem = async (itemId: string) => {
    await deleteWorkBasketItems({
      serviceCaseId,
      serviceOrderId: orderId,
      serviceJobId: addLabourJobId,
      body: {serviceItemId: [itemId]},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const removeItems = (items: LabourBasketItemType[]) =>
    deleteWorkBasketItems({
      serviceCaseId,
      serviceOrderId: orderId,
      serviceJobId: addLabourJobId,
      body: {serviceItemId: items.map((item) => item.id)},
    })
      .unwrap()
      .catch(handleApiError);

  const openItemsSettings = (items: LabourBasketItemType[]) => {
    openDialog(
      <BulkEditItem
        items={items}
        onClose={() => closeDialog('bulkEditBasketItem')}
        serviceCaseId={serviceCaseId}
        serviceOrderId={orderId}
        serviceJobId={addLabourJobId}
        data-testid={suffixTestId('editItem', props)}
      />,
      {
        title: i18n.t('entity.addWork.actions.itemsSettings'),
        id: 'bulkEditBasketItem',
      }
    );
  };

  const actions = buildArray<ButtonProps>()
    .add({
      isLoading: isDeletingBasketItems,
      isDisabled: hasPendingRequest,
      title: i18n.t('general.actions.discard'),
      variant: 'secondary',
      onClick: handleCancel,
      'data-testid': suffixTestId('cancel', props),
    })
    .add({
      isLoading: isCheckingOutBasket,
      isDisabled: isBasketEmpty || hasPendingRequest || hasUnsettledQuantity,
      title: i18n.t('general.actions.add'),
      keyboardShortcut: KeyboardShortcut.ModEnter,
      onClick: handleCheckout,
      'data-testid': suffixTestId('add', props),
    });

  return (
    <FullScreenModal
      headline={i18n.t('entity.addWork.actions.addWork')}
      parameters={headerParameters}
      actions={actions}
    >
      <Box padding={4} height="100%">
        <HStack spacing={4} height="100%">
          <Box flex={5}>
            <ItemListCard
              onAddToBasket={handleAddLabourItem}
              data-testid={suffixTestId('itemList', props)}
            />
          </Box>
          <Box flex={1} minWidth={86}>
            <AfterSalesLabourBasket<LabourBasketItemType>
              focusTrigger={focusTrigger}
              basket={{
                items: basketItems,
                totalPrice: basket?.workBasketTotalPrice,
                isLoading: isBasketLoading,
                hasError: hasBasketError,
                hasPendingRequest,
                hasInvalidItems: hasInvalidBasketItems,
              }}
              mechanic={{
                mechanics,
                areMechanicsLoading,
                selectedMechanicId: inputMechanicId,
                onMechanicChange: handleMechanicChange,
              }}
              bulkActions={[
                {action: openItemsSettings, label: i18n.t('entity.addWork.actions.itemsSettings')},
                {action: removeItems, label: i18n.t('general.actions.remove')},
              ]}
              onQuantityChange={handleQuantityChange}
              onEdit={handleEditLabourItem}
              onDelete={handleDeleteLabourItem}
              onTabOut={() => focusGridPart(WORK_CATALOGUE_GRID_CODE, 'smartSearch')}
              data-testid={suffixTestId('basket', props)}
            />
          </Box>
        </HStack>
      </Box>
    </FullScreenModal>
  );
}
